import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useClient } from '../../features/clients/hooks/useClient';
import { useTasks } from '../../context/TasksContext';
import ProfileHeader from '../../features/clients/components/profile/ProfileHeader';
import ProfileTabs from '../../features/clients/components/profile/ProfileTabs';
import FinancialSummaryCard from '../../features/clients/components/profile/FinancialSummaryCard';

/**
 * ClientDetailPage - Unified Account Profile page
 * 
 * Serves as the canonical profile for both Leads and Clients.
 * The `entryContext` prop determines navigation behavior:
 * - "leads" → Back to Leads list
 * - "clients" (default) → Back to Clients list
 * 
 * Data source is the same (companies/{companyId}/leads/) regardless of entry point.
 */
export default function ClientDetailPage({ entryContext = 'clients' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { client, loading, error, notFound } = useClient(id);
  const { ensureLeadTasks } = useTasks();

  // Profile tab is controlled here so the financial summary can jump to Payments.
  // A `?tab=` query param lets deep links (e.g. WorkOrderSheet "View in Payments")
  // land directly on a specific tab. Falls back to Overview for unknown values.
  const VALID_TABS = ['overview', 'property', 'activity', 'jobs', 'quotes', 'payments', 'notes', 'lifecycle'];
  const [searchParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const tabsRef = useRef(null);

  const handleViewInvoices = () => {
    setActiveTab('payments');
    tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Load this lead's full job history (profile shows tasks beyond the
  // bounded global window)
  useEffect(() => { ensureLeadTasks(id); }, [id, ensureLeadTasks]);

  // Dynamic back navigation based on entry context
  const backPath = entryContext === 'leads' ? '/leads' : '/clients';
  const backLabel = entryContext === 'leads' ? 'Back to Leads' : 'Back to Clients';
  const entityLabel = entryContext === 'leads' ? 'Lead' : 'Client';

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(backPath)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading {entityLabel.toLowerCase()} profile...</span>
          </div>
        </div>
      </div>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(backPath)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">{entityLabel} Not Found</h1>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <p className="text-gray-600 font-medium">{entityLabel} not found</p>
            <p className="text-sm text-gray-500 mt-1">
              The {entityLabel.toLowerCase()} you're looking for doesn't exist or has been deleted.
            </p>
            <Button
              onClick={() => navigate(backPath)}
              className="mt-4 text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              {backLabel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(backPath)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Error</h1>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <p className="text-red-600 font-medium">Error loading {entityLabel.toLowerCase()}</p>
            <p className="text-sm text-gray-500 mt-1">{error}</p>
            <Button
              onClick={() => navigate(backPath)}
              className="mt-4 text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              {backLabel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success state - render profile
  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate(backPath)}
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          {backLabel}
        </Button>
      </div>

      {/* Profile Header */}
      <ProfileHeader client={client} entryContext={entryContext} />

      {/* Financial Summary — at-a-glance outstanding balance / open invoices / card */}
      <FinancialSummaryCard client={client} onViewInvoices={handleViewInvoices} />

      {/* Profile Tabs */}
      <div ref={tabsRef}>
        <ProfileTabs client={client} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
    </div>
  );
}
